import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Shield, Search, AlertCircle, FileText, HelpCircle, CheckCircle, XCircle } from 'lucide-react';

const HowToCheckGAPMisSoldPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="py-20 lg:py-28 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">Blue Lion Law</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6">
                How to Check if Your GAP Insurance Was Mis-Sold
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                The signs of mis-selling, and why your claim may be stronger than you think
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <p className="text-xl text-foreground leading-relaxed mb-6">
                If you bought GAP insurance from a car dealership or finance provider, there's a strong 
                chance it was mis-sold. The question isn't whether mis-selling happened — the FCA has 
                already confirmed it was widespread. <strong>The question is whether it happened to you.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Core Issue Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <AlertCircle className="w-8 h-8 text-gold" />
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground">
                  The Core Issue: Hidden Commission
                </h2>
              </div>
              
              <p className="text-lg text-primary-foreground/80 mb-6">
                At the heart of the GAP insurance scandal is one simple fact:
              </p>

              <Card className="bg-primary-foreground/10 border-none mb-6">
                <CardContent className="p-6">
                  <p className="text-xl font-bold text-primary-foreground text-center">
                    Dealers were earning up to <span className="text-gold">70% of every premium</span> in commission, 
                    and they didn't tell you.
                  </p>
                </CardContent>
              </Card>

              <p className="text-lg text-primary-foreground/80">
                If you'd known that only 30p of every pound you paid was actually going towards your 
                insurance cover — with the rest going into the dealer's pocket — would you have bought it? 
                Would you have at least shopped around?
              </p>
              <p className="text-lg text-primary-foreground/90 mt-4">
                <strong>The fact that you weren't given this information is the foundation of most GAP insurance 
                mis-selling claims.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Signs Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <Search className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  Signs Your GAP Insurance Was Mis-Sold
                </h2>
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm">1</span>
                    You Weren't Told About the Commission
                  </h3>
                  <Card className="border-l-4 border-l-accent">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground">
                        This is the big one. In the vast majority of cases, dealers never disclosed that they 
                        were earning 50-70% of the premium as commission. <strong className="text-foreground">If you weren't told, your policy was 
                        likely mis-sold.</strong>
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm">2</span>
                    You Weren't Told GAP Was Optional
                  </h3>
                  <Card className="border-l-4 border-l-gold">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground">
                        Some dealers implied — or even stated — that GAP insurance was required to get the 
                        finance. It wasn't. GAP insurance has always been optional. If you felt pressured or 
                        believed you had no choice, that's a red flag.
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm">3</span>
                    You Weren't Told You Could Buy It Elsewhere
                  </h3>
                  <Card className="border-l-4 border-l-accent">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground">
                        The exact same GAP cover (or better) was available online for a fraction of the 
                        dealership price. A policy costing <strong className="text-foreground">£400 at the dealership might have cost £80 elsewhere</strong>. 
                        You should have been told this. Were you?
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm">4</span>
                    You Were Pressured at the Point of Sale
                  </h3>
                  <Card className="border-l-4 border-l-gold">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground">
                        GAP insurance was typically sold at the end of a long buying process, when you were 
                        tired and eager to drive away in your new car. This timing wasn't accidental — <strong className="text-foreground">it was 
                        designed to reduce your resistance</strong> and prevent you from shopping around.
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm">5</span>
                    The Policy Wasn't Properly Explained
                  </h3>
                  <Card className="border-l-4 border-l-accent">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground">
                        Did you fully understand what you were buying? Were the terms, conditions, and exclusions 
                        clearly explained? Or was GAP insurance presented as a simple "peace of mind" product 
                        without proper detail?
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm">6</span>
                    You Didn't Actually Need It
                  </h3>
                  <Card className="border-l-4 border-l-gold">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground">
                        GAP insurance isn't appropriate for everyone. If you put down a large deposit, had a 
                        short finance term, or your standard car insurance already covered the gap, you may not 
                        have needed it at all. A responsible seller should have assessed your needs — not just 
                        pushed the sale.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ask Yourself Section */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-gold" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  Ask Yourself These Questions
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Did anyone mention how much commission the dealer was earning?",
                  "Were you told you could buy GAP insurance elsewhere, possibly cheaper?",
                  "Did you feel you had a genuine choice about whether to buy?",
                  "Were you given time to think about it, or was it rushed through at the end?",
                  "Did you understand exactly what the policy covered and excluded?",
                  "Were you told about the 14-day cooling-off period?"
                ].map((question, idx) => (
                  <Card key={idx} className="border-border">
                    <CardContent className="p-4 flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                      <p className="text-foreground text-sm">{question}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-gold/5 border-gold mt-8">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    If you answered <strong>"no"</strong> to any of these questions, your GAP insurance may have been mis-sold.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Don't Have Paperwork Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  Don't Have Your Paperwork? Don't Worry.
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-6">
                Many people don't keep their GAP insurance documents for years. That's fine. 
              </p>

              <Card className="bg-accent/5 border-accent">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                    <p className="text-foreground">
                      <strong>Blue Lion Law can request the necessary information</strong> from the finance company 
                      and dealer as part of the claims process. You don't need to have everything 
                      in hand to get started.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FCA Groundwork */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                The FCA Has Already Done the Groundwork
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                The Financial Conduct Authority has found that GAP insurance policies sold at dealerships 
                <strong className="text-foreground"> "do not provide fair value"</strong> to consumers. They've effectively confirmed that the 
                industry-wide practices were problematic.
              </p>

              <Card className="bg-accent/5 border-accent">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    This makes individual claims stronger — <strong>the mis-selling wasn't isolated, it was systematic.</strong>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What Happens Next */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-8">
                What Happens Next?
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8">
                If you believe your GAP insurance was mis-sold:
              </p>

              <div className="space-y-4">
                {[
                  { step: "1", title: "Get a professional assessment", desc: "Blue Lion Law can review your circumstances" },
                  { step: "2", title: "We gather the evidence", desc: "We can request documents from the relevant parties" },
                  { step: "3", title: "We pursue your claim", desc: "On a no win, no fee basis" },
                  { step: "4", title: "You receive compensation", desc: "If your claim is successful" }
                ].map((item, idx) => (
                  <Card key={idx} className="border-l-4 border-l-accent">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center flex-shrink-0 text-white font-bold">
                        {item.step}
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground">{item.title}</h3>
                        <p className="text-muted-foreground">{item.desc}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-gold/5 border-gold mt-8">
                <CardContent className="p-6">
                  <p className="text-foreground font-medium">
                    Don't let history repeat itself. With PPI, millions of valid claims were never made. 
                    <strong> Don't miss your opportunity with GAP insurance.</strong>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground mb-6">
                Ready to Check Your Claim?
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8">
                It takes a few minutes to start your claim.
              </p>
              <Button variant="gold" size="lg" asChild>
                <a href="/gap-insurance-claims">
                  Start Your Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background border-t border-border">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-lg font-bold text-foreground mb-6">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <a href="/gap-insurance/fca-action-on-gap-insurance" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">FCA Action on GAP Insurance</span>
                </a>
                <a href="/gap-insurance/gap-insurance-claims-faq" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">GAP Insurance Claims FAQ</span>
                </a>
                <a href="/gap-insurance-claims" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">GAP Insurance Claims</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HowToCheckGAPMisSoldPage;
